import { z } from "zod";
import type { RouterOutputs } from "@remi/api";
import type { EscalationRiskTier } from "@remi/enums/escalation-risk-tier";
import { EscalationRiskTierValues } from "@remi/enums/escalation-risk-tier";
import { TaskStatus } from "@remi/enums/task-status";
import {
  activeTaskCondition,
  type ConditionState,
  emptyConditionState,
  encodeConditions,
  getConversationDefinition,
  getDefaultConditions,
  getEscalationDefinition,
  getInboxSearchInput,
  getSortDefinition,
  getTaskDefinition,
  getTicketDefinition,
  isTeamScopedInbox,
} from "@remi/inbox-queues/conditions";
import { entries, isOneOf } from "@remi/ts-utils";
import { parseRelativeDateToken } from "@remi/types/inbox";
import {
  Comparator,
  type Condition,
  ConditionSchema,
  type Definition,
  LogicalOperator,
  MySqlValue,
} from "@remi/types/query-builder";
import { InboxItemType, InboxItemTypeSchema } from "@remi/types/table";

export type { ConditionState };
export {
  activeTaskCondition,
  emptyConditionState,
  encodeConditions,
  getConversationDefinition,
  getDefaultConditions,
  getEscalationDefinition,
  getInboxSearchInput,
  getSortDefinition,
  getTaskDefinition,
  getTicketDefinition,
  isTeamScopedInbox,
};

export type ConditionAction =
  | { type: "UPDATE_CONDITION_FIELD"; payload: { field: keyof ConditionState; condition?: Condition } }
  | { type: "SET_CONDITION_STATE"; payload: { state: ConditionState } }
  | { type: "RESET_CONDITION_STATE_TO_DEFAULT"; payload?: undefined };

const optionalCondition = ConditionSchema.optional();

const ConditionStateSchema = z
  .object({
    status: optionalCondition,
    priority: optionalCondition,
    assigneeId: optionalCondition,
    teamId: optionalCondition,
    subcategory: optionalCondition,
    type: optionalCondition,
    conversationType: optionalCondition,
    conversationChannel: optionalCondition,
    partnerFlow: optionalCondition,
    snoozed: optionalCondition,
    dueDate: optionalCondition,
    isResolved: optionalCondition,
    needsCheckIn: optionalCondition,
    createdById: optionalCondition,
    taskStatus: optionalCondition,
    readiness: optionalCondition,
    needsReview: optionalCondition,
    taskType: optionalCondition,
    projectCategory: optionalCondition,
    homeownerSentiment: optionalCondition,
    tagId: optionalCondition,
    riskTier: optionalCondition,
    resolutionStage: optionalCondition,
    hasOpenConversation: optionalCondition,
    contractorId: optionalCondition,
    waitingOn: optionalCondition,
    latestItemKind: optionalCondition,
    latestItemStormTag: optionalCondition,
    createdAt: optionalCondition,
    updatedAt: optionalCondition,
    followUpDate: optionalCondition,
    lastUnresolvedAt: optionalCondition,
    snoozeDate: optionalCondition,
  } satisfies Record<keyof ConditionState, typeof optionalCondition>)
  .strict();

export const decodeConditions = (value: unknown): ConditionState | null => {
  // Bad/stale shape → null so the caller falls back to its own defaults; returning empty would
  // instead drop every filter and surface every item in scope.
  const result = ConditionStateSchema.safeParse(value);
  if (!result.success) {
    return null;
  }
  // The slot key is authoritative: re-stamp each condition's `field` from its key so a hand-edited
  // URL can't file a condition under a mismatched slot (the backend filters on the inner `field`).
  const restamped: Partial<ConditionState> = {};
  for (const [field, condition] of entries(result.data)) {
    if (condition) {
      restamped[field] = { ...condition, field };
    }
  }
  return { ...emptyConditionState, ...restamped };
};

export const hasActiveConditions = (conditions: ConditionState): boolean => Object.values(conditions).some(Boolean);

type ConditionField = keyof ConditionState;

const ALL_INBOX_ITEM_TYPE_COUNT = InboxItemTypeSchema.options.length;

const getTypeCondition = (view: RouterOutputs["communication"]["listInboxViews"][number]): Condition | undefined => {
  const present: InboxItemType[] = [];
  if (view.ticketDefinition) {
    present.push(InboxItemType.TICKET);
  }
  if (view.conversationDefinition) {
    present.push(InboxItemType.CONVERSATION);
  }
  if (view.taskDefinition) {
    present.push(InboxItemType.TASK);
  }
  if (view.escalationDefinition) {
    present.push(InboxItemType.ESCALATION);
  }

  if (present.length === 0 || present.length === ALL_INBOX_ITEM_TYPE_COUNT) {
    return undefined;
  }
  const [only] = present;
  if (present.length === 1 && only) {
    return { field: "type", value: only, comparator: Comparator.EQUALS };
  }
  return { field: "type", value: present, comparator: Comparator.IN };
};

// Relative-token groups reuse the same followUpDate field the snooze/dueDate toggles rewrite to, so
// both extractors discriminate by VALUE: every condition in a relative group parses as a NOW±offset
// token, while toggle groups carry NULL/NOW/boundary sentinels.
const isRelativeDateGroup = (node: Condition | Definition): boolean =>
  "operator" in node &&
  node.conditions.length > 0 &&
  node.conditions.every((c) => "field" in c && typeof c.value === "string" && parseRelativeDateToken(c.value) !== null);

const findToggleGroup = (definition: Definition | null | undefined, field: "followUpDate" | "snoozedUntil") =>
  definition?.conditions.find(
    (c) =>
      "operator" in c &&
      !isRelativeDateGroup(c) &&
      c.conditions.find((d: Condition | Definition) => "field" in d && d.field === field),
  );

const findFollowUpDateGroup = (definition: Definition | null | undefined) =>
  findToggleGroup(definition, "followUpDate");

const findRelativeDateGroup = (definition: Definition | null | undefined, field: string): Definition | undefined => {
  const group = definition?.conditions.find(
    (c) => "operator" in c && isRelativeDateGroup(c) && c.conditions.every((d) => "field" in d && d.field === field),
  );
  return group && "operator" in group ? group : undefined;
};

const findScalarTokenCondition = (definition: Definition | null | undefined, field: string): Condition | undefined => {
  const node = definition?.conditions.find(
    (c) =>
      "field" in c &&
      c.field === field &&
      (c.comparator === Comparator.LESS_THAN || c.comparator === Comparator.GREATER_THAN_OR_EQUAL) &&
      typeof c.value === "string" &&
      parseRelativeDateToken(c.value) !== null,
  );
  return node && "field" in node ? node : undefined;
};

const extractRelativeDateCondition = (
  definitions: (Definition | null | undefined)[],
  sqlField: string,
  slotField: string = sqlField,
): Condition | undefined => {
  for (const definition of definitions) {
    const scalar = findScalarTokenCondition(definition, sqlField);
    if (scalar) {
      return { ...scalar, field: slotField };
    }
    const group = findRelativeDateGroup(definition, sqlField);
    if (!group) {
      continue;
    }
    const getTokenForComparator = (comparator: Comparator): string | undefined => {
      const node = group.conditions.find((c) => "comparator" in c && c.comparator === comparator);
      return node && "value" in node && typeof node.value === "string" ? node.value : undefined;
    };
    const gteToken = getTokenForComparator(Comparator.GREATER_THAN_OR_EQUAL);
    if (!gteToken) {
      continue;
    }
    const ltToken = getTokenForComparator(Comparator.LESS_THAN);
    return { field: slotField, comparator: Comparator.IN, value: ltToken ? [gteToken, ltToken] : [gteToken] };
  }
  return undefined;
};

export const extractConditionsFromView = (
  view: RouterOutputs["communication"]["listInboxViews"][number],
): ConditionState => {
  const typeDefinition = getTypeCondition(view);
  const snoozedDefinition =
    findFollowUpDateGroup(view.ticketDefinition) ||
    findFollowUpDateGroup(view.conversationDefinition) ||
    findToggleGroup(view.escalationDefinition, "snoozedUntil");

  const dueDateDefinition = findFollowUpDateGroup(view.taskDefinition);

  const sentimentDefinition =
    view.ticketDefinition?.conditions.find(
      (c) =>
        "operator" in c &&
        c.conditions.find((d: Condition | Definition) => "field" in d && d.field === "homeownerSentiment"),
    ) ||
    view.conversationDefinition?.conditions.find(
      (c) =>
        "operator" in c &&
        c.conditions.find((d: Condition | Definition) => "field" in d && d.field === "homeownerSentiment"),
    ) ||
    view.taskDefinition?.conditions.find(
      (c) =>
        "operator" in c &&
        c.conditions.find((d: Condition | Definition) => "field" in d && d.field === "homeownerSentiment"),
    );

  const findCondition = (definition: Definition | null | undefined, field: Condition["field"]) => {
    const condition = definition?.conditions.find((entry) => "field" in entry && entry.field === field);
    if (condition && "field" in condition) {
      return condition;
    }
    return undefined;
  };

  const readinessCondition = findCondition(view.taskDefinition, "readiness");
  const isNeedsReviewNode = (entry: Condition): boolean =>
    entry.comparator === Comparator.IN &&
    Array.isArray(entry.value) &&
    entry.value.length === 1 &&
    entry.value[0] === TaskStatus.NEEDS_REVIEW;
  const taskStatusEntries = (view.taskDefinition?.conditions ?? []).filter(
    (entry): entry is Condition => "field" in entry && entry.field === "taskStatus",
  );
  // The toggle emits a separate `taskStatus IN [NEEDS_REVIEW]` predicate alongside the primary task
  // filter — a taskStatus set pre-cutover, or a readiness set after the backfill rewrites it. Split it
  // back out so the toggle stays visible; a lone entry with no primary filter is still a plain filter.
  const hasPrimaryFilter = taskStatusEntries.length > 1 || readinessCondition !== undefined;
  const needsReviewEntry = hasPrimaryFilter ? taskStatusEntries.find(isNeedsReviewNode) : undefined;
  const taskStatusCondition = needsReviewEntry
    ? taskStatusEntries.find((entry) => entry !== needsReviewEntry)
    : taskStatusEntries[0];

  const conditionState = {
    status: findCondition(view.ticketDefinition, "status"),
    taskStatus: taskStatusCondition,
    readiness: readinessCondition,
    needsReview: needsReviewEntry
      ? { field: "needsReview", value: MySqlValue.TRUE, comparator: Comparator.EQUALS }
      : undefined,
    taskType: findCondition(view.taskDefinition, "taskType"),
    projectCategory: findCondition(view.taskDefinition, "projectCategory"),
    priority: findCondition(view.ticketDefinition, "priority") ?? findCondition(view.escalationDefinition, "priority"),
    assigneeId:
      findCondition(view.ticketDefinition, "assigneeId") ??
      findCondition(view.conversationDefinition, "assigneeId") ??
      findCondition(view.taskDefinition, "assigneeId") ??
      findCondition(view.escalationDefinition, "assigneeId"),
    teamId:
      findCondition(view.ticketDefinition, "teamId") ??
      findCondition(view.conversationDefinition, "teamId") ??
      findCondition(view.taskDefinition, "teamId") ??
      findCondition(view.escalationDefinition, "teamId"),
    subcategory: findCondition(view.ticketDefinition, "subcategory"),
    conversationType: findCondition(view.conversationDefinition, "conversationType"),
    conversationChannel: findCondition(view.conversationDefinition, "conversationChannel"),
    partnerFlow:
      findCondition(view.ticketDefinition, "partnerFlow") ??
      findCondition(view.conversationDefinition, "partnerFlow") ??
      findCondition(view.taskDefinition, "partnerFlow"),
    snoozed:
      snoozedDefinition && "operator" in snoozedDefinition ? extractSnoozeDefinition(snoozedDefinition) : undefined,
    dueDate:
      dueDateDefinition && "operator" in dueDateDefinition ? extractDueDateDefinition(dueDateDefinition) : undefined,
    type: typeDefinition,
    createdById: findCondition(view.ticketDefinition, "createdById"),
    isResolved: findCondition(view.conversationDefinition, "isResolved"),
    needsCheckIn: findCondition(view.conversationDefinition, "needsCheckIn"),
    homeownerSentiment:
      sentimentDefinition && "operator" in sentimentDefinition
        ? extractSentimentDefinition(sentimentDefinition)
        : undefined,
    tagId: findCondition(view.ticketDefinition, "tagId"),
    riskTier: findCondition(view.escalationDefinition, "riskTier"),
    resolutionStage: findCondition(view.escalationDefinition, "resolutionStage"),
    hasOpenConversation: findCondition(view.escalationDefinition, "hasOpenConversation"),
    contractorId:
      findCondition(view.ticketDefinition, "contractorId") ??
      findCondition(view.conversationDefinition, "contractorId") ??
      findCondition(view.taskDefinition, "contractorId") ??
      findCondition(view.escalationDefinition, "contractorId"),
    waitingOn: findCondition(view.taskDefinition, "waitingOn"),
    latestItemKind: findCondition(view.conversationDefinition, "latestItemKind"),
    latestItemStormTag: findCondition(view.conversationDefinition, "latestItemStormTag"),
    createdAt: extractRelativeDateCondition(
      [view.ticketDefinition, view.conversationDefinition, view.taskDefinition, view.escalationDefinition],
      "createdAt",
    ),
    updatedAt: extractRelativeDateCondition(
      [view.ticketDefinition, view.conversationDefinition, view.taskDefinition, view.escalationDefinition],
      "updatedAt",
    ),
    followUpDate: extractRelativeDateCondition([view.taskDefinition, view.escalationDefinition], "followUpDate"),
    lastUnresolvedAt: extractRelativeDateCondition([view.conversationDefinition], "lastUnresolvedAt"),
    snoozeDate: extractRelativeDateCondition([view.ticketDefinition], "followUpDate", "snoozeDate"),
  } satisfies ConditionState;

  return conditionState;
};

/**
 * This makes me less than happy. This is a hack due to the
 * limitations of the UI components that currently exist for filtering.
 * We need to convert the snooze definition to something the existing
 * front end will understand
 */
const extractSnoozeDefinition = (definition: Definition): Condition | undefined => {
  if (definition.conditions.find((c) => "comparator" in c && c.comparator === Comparator.LESS_THAN)) {
    return {
      field: "snoozed",
      value: MySqlValue.FALSE,
      comparator: Comparator.EQUALS,
    };
  } else {
    return {
      field: "snoozed",
      value: MySqlValue.TRUE,
      comparator: Comparator.EQUALS,
    };
  }
};

const hasComparator = (definition: Definition, comparator: Comparator): boolean =>
  definition.conditions.some((c) => "comparator" in c && c.comparator === comparator);

export const isMatchAllFollowUpDate = (definition: Definition): boolean =>
  definition.operator === LogicalOperator.OR &&
  definition.conditions.every((c) => "value" in c && c.value === MySqlValue.NULL) &&
  hasComparator(definition, Comparator.EQUALS) &&
  hasComparator(definition, Comparator.NOT_EQUALS);

const extractDueDateDefinition = (definition: Definition): Condition | undefined => {
  if (isMatchAllFollowUpDate(definition)) {
    return { field: "dueDate", value: [MySqlValue.TRUE, MySqlValue.FALSE], comparator: Comparator.IN };
  }
  if (hasComparator(definition, Comparator.LESS_THAN)) {
    return { field: "dueDate", value: MySqlValue.TRUE, comparator: Comparator.EQUALS };
  }
  if (hasComparator(definition, Comparator.GREATER_THAN_OR_EQUAL)) {
    return { field: "dueDate", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
  }
  return undefined;
};

const extractSentimentValueFromRange = (conditions: (Condition | Definition)[]): string | undefined => {
  const rangeToSentimentValue = {
    "0-24": "0-2",
    "25-44": "3-4",
    "45-64": "5-6",
    "65-84": "7-8",
    "85-100": "9-10",
  } as const;

  // Type guard function to check if an item is a Condition
  const isCondition = (item: Condition | Definition): item is Condition => {
    return "field" in item;
  };

  // Filter conditions to get only Condition types
  const conditionItems = conditions.filter(isCondition);

  const minCondition = conditionItems.find(
    (c) => c.field === "homeownerSentiment" && c.comparator === Comparator.GREATER_THAN_OR_EQUAL,
  );

  const maxCondition = conditionItems.find(
    (c) => c.field === "homeownerSentiment" && c.comparator === Comparator.LESS_THAN_OR_EQUAL,
  );

  if (
    minCondition &&
    maxCondition &&
    typeof minCondition.value === "number" &&
    typeof maxCondition.value === "number"
  ) {
    const rangeKey = `${minCondition.value}-${maxCondition.value}`;
    switch (rangeKey) {
      case "0-24":
        return rangeToSentimentValue["0-24"];
      case "25-44":
        return rangeToSentimentValue["25-44"];
      case "45-64":
        return rangeToSentimentValue["45-64"];
      case "65-84":
        return rangeToSentimentValue["65-84"];
      case "85-100":
        return rangeToSentimentValue["85-100"];
      default:
        return undefined;
    }
  }

  return undefined;
};

const extractSentimentDefinition = (definition: Definition): Condition | undefined => {
  // Handle single range (from EQUALS)
  if (definition.operator === LogicalOperator.AND) {
    const sentimentValue = extractSentimentValueFromRange(definition.conditions);
    if (sentimentValue) {
      return {
        field: "homeownerSentiment",
        value: sentimentValue,
        comparator: Comparator.EQUALS,
      };
    }
  }

  // Handle multiple ranges (from IN)
  if (definition.operator === LogicalOperator.OR) {
    const sentimentValues: string[] = [];

    definition.conditions.forEach((condition) => {
      if ("operator" in condition && condition.operator === LogicalOperator.AND) {
        const sentimentValue = extractSentimentValueFromRange(condition.conditions);
        if (sentimentValue) {
          sentimentValues.push(sentimentValue);
        }
      }
    });

    if (sentimentValues[0]) {
      return {
        field: "homeownerSentiment",
        value: sentimentValues.length === 1 ? sentimentValues[0] : sentimentValues,
        comparator: sentimentValues.length === 1 ? Comparator.EQUALS : Comparator.IN,
      };
    }
  }

  return undefined;
};

const ConditionToType: Record<ConditionField, InboxItemType[]> = {
  type: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK, InboxItemType.ESCALATION],
  // Escalations are all created with a fixed Actionable.priority (0/URGENT); risk tier is their real
  // severity axis, so the priority filter is offered for tickets only, not escalation views.
  priority: [InboxItemType.TICKET],
  status: [InboxItemType.TICKET],
  taskStatus: [InboxItemType.TASK],
  readiness: [InboxItemType.TASK],
  needsReview: [InboxItemType.TASK],
  taskType: [InboxItemType.TASK],
  projectCategory: [InboxItemType.TASK],
  isResolved: [InboxItemType.CONVERSATION],
  needsCheckIn: [InboxItemType.CONVERSATION],
  conversationType: [InboxItemType.CONVERSATION],
  conversationChannel: [InboxItemType.CONVERSATION],
  partnerFlow: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK],
  assigneeId: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK, InboxItemType.ESCALATION],
  teamId: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK, InboxItemType.ESCALATION],
  subcategory: [InboxItemType.TICKET],
  snoozed: [InboxItemType.TICKET, InboxItemType.ESCALATION],
  dueDate: [InboxItemType.TASK],
  createdById: [InboxItemType.TICKET],
  homeownerSentiment: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK],
  tagId: [InboxItemType.TICKET],
  riskTier: [InboxItemType.ESCALATION],
  resolutionStage: [InboxItemType.ESCALATION],
  hasOpenConversation: [InboxItemType.ESCALATION],
  contractorId: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK, InboxItemType.ESCALATION],
  waitingOn: [InboxItemType.TASK],
  latestItemKind: [InboxItemType.CONVERSATION],
  latestItemStormTag: [InboxItemType.CONVERSATION],
  createdAt: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK, InboxItemType.ESCALATION],
  updatedAt: [InboxItemType.TICKET, InboxItemType.CONVERSATION, InboxItemType.TASK, InboxItemType.ESCALATION],
  followUpDate: [InboxItemType.TASK, InboxItemType.ESCALATION],
  lastUnresolvedAt: [InboxItemType.CONVERSATION],
  snoozeDate: [InboxItemType.TICKET],
};

export const showCondition = ({
  field,
  conditions,
  superConditions,
}: {
  field: ConditionField;
  conditions: ConditionState;
  superConditions: ConditionState;
}) => {
  if (superConditions[field]) {
    return false;
  }
  const typeCondition = superConditions.type ?? conditions.type;
  if (!typeCondition) {
    return true;
  }
  let selectiveTypes: string[] = [];
  if (typeCondition.comparator === Comparator.EQUALS) {
    const value = z
      .string()
      .optional()
      .catch(undefined)
      .parse(typeCondition.value as unknown);
    if (value) {
      selectiveTypes.push(value);
    }
  } else if (typeCondition.comparator === Comparator.IN) {
    selectiveTypes = Array.isArray(typeCondition.value) ? typeCondition.value : [];
  }
  const types =
    selectiveTypes.length > 0
      ? z
          .array(InboxItemTypeSchema)
          .catch([])
          .parse(selectiveTypes as unknown)
      : [];
  if (types.length === 0) {
    return true;
  }
  for (const type of types) {
    if (ConditionToType[field].includes(type)) {
      return true;
    }
  }
  return false;
};

// The tiers a view pins as its baseline (`riskTier IN [...]`), used to bound the filter so the user
// can only narrow within the view's set, never widen past it.
export const getPinnedRiskTiers = (
  superConditions: ConditionState | undefined,
): readonly EscalationRiskTier[] | undefined => {
  const condition = superConditions?.riskTier;
  if (!condition || condition.comparator !== Comparator.IN || !Array.isArray(condition.value)) {
    return undefined;
  }
  const tiers = condition.value.filter((value): value is EscalationRiskTier =>
    isOneOf(value, EscalationRiskTierValues),
  );
  return tiers.length > 0 ? tiers : undefined;
};
